import { useSaveData } from '@entities/common/savedData/index.ts';
import { handleNotAvailableByScheduleVideoClick } from '@entities/exercise-video/helpers.ts';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuthModal } from '@features/common/protected-auth-route/model.ts';
import { useProgramModalBuyState } from '@features/programs/buy-program/model.ts';
import { getIsVideoAvailableBySchedule } from '@shared/helpers/index.ts';
import { useAppDispatch, useMobile, useUser, useWindowWidth } from '@shared/hooks';
import { useCurrentPosRtk } from '@shared/hooks/use-current-pos.ts';
import type { ExerciseVideo } from '@shared/types/exercises';
import type { Program, RecommendationT } from '@shared/types/programs';
import type { PromoVideo } from '@shared/types/promo-videos';
import type { Mode } from 'library/types/programs';

const useVideoClick = (program?: Program) => {
  const windowWidth = useWindowWidth();
  const { openAuth } = useAuthModal();
  const { isMobile } = useMobile();
  const { doOpenModal, doCloseModal } = useProgramModalBuyState(program?.pk);
  const videoBlockRef = useRef<HTMLElement>(null);
  const dispatch = useAppDispatch();

  const { user, isAuth } = useUser();
  const defaultMode = windowWidth > 768 ? 'promo' : undefined;

  const [mode, setMode] = useState<Mode>(defaultMode);
  const changeMode = (mode: Mode) => {
    setMode(mode);
  };

  const {
    data: id,
    doSetStorageData: setId,
    doRemoveStorageData: removeVideoId,
  } = useSaveData<number>('videoId');

  const [description, setDescription] = useState('');
  const [about, setAbout] = useState('О программе');

  const isAuthor = useMemo(() => program?.author.id === user?.id, [user, program]);

  const [currentVideoInPlayer, setCurrentVideoInPlayer] = useState(() => {
    const video =
      program?.videos.find((el) => el.id === Number(id)) ||
      program?.promo_video ||
      program?.videos[0];
    // console.log('videoId:', id);
    // console.log(
    //   'programVideo:',
    //   program?.videos.find((el) => el.id === id)
    // );
    // console.log('video:', video);
    if (video) {
      setId(video.id);
      setDescription(video.description);
      setAbout(video.title);

      if (windowWidth > 768) {
        if (video.id === program?.promo_video?.id) {
          setMode('promo');
        } else {
          setMode('video');
        }
      } else {
        setMode(undefined);
      }
    }
    return video;
  });

  // useEffect(() => {
  //   console.log('currentVideoInPlayer: ', currentVideoInPlayer);
  // }, [currentVideoInPlayer]);

  const [currentActiveRecommendation, setCurrentActiveRecommendation] = useState<
    RecommendationT | undefined
  >(undefined);

  const getIsVideoAvailableByScheduleFunc = useCallback(
    (video?: ExerciseVideo | PromoVideo) => {
      if (isAuthor) {
        return true;
      }
      return getIsVideoAvailableBySchedule({ program, video });
    },
    [program, isAuthor]
  );

  const onNotAvailableByScheduleVideoClick = useCallback(
    (video?: ExerciseVideo | PromoVideo) => {
      const lookedSchedule = program?.schedules?.find((el) => el.videoId === video?.id);
      if (lookedSchedule) {
        dispatch(handleNotAvailableByScheduleVideoClick(lookedSchedule.date));
      }
    },
    [dispatch, program]
  );

  const isCurrentVideoAvailableBySchedule = useMemo(() => {
    if (isAuthor) {
      return true;
    }
    return getIsVideoAvailableByScheduleFunc(currentVideoInPlayer);
  }, [getIsVideoAvailableByScheduleFunc, currentVideoInPlayer, isAuthor]);

  const onCurrentVideoNotAvailableClick = useCallback(
    () => onNotAvailableByScheduleVideoClick(currentVideoInPlayer),
    [currentVideoInPlayer, onNotAvailableByScheduleVideoClick]
  );

  const { saveCurrentPos, scrollToSavedPos } = useCurrentPosRtk({
    withScrollToSavedPos: true,
    nameInStorage: 'ProgramLargeCardScrollY',
  });

  const onAboutClick = useCallback(() => {
    if (!program) return;
    if (program.promo_video && typeof program.promo_video !== 'number') {
      saveCurrentPos();
      setId(program.promo_video.id);
      changeMode('promo');
    }

    setDescription(program.description);
    setAbout('О программе');
    setCurrentActiveRecommendation(undefined);

    if (!program.promo_video || typeof program.promo_video === 'number') return;
    const { promo_video } = program;
    setCurrentVideoInPlayer(promo_video);
  }, [program]);

  const isNeedPay = useMemo(() => {
    return !program?.is_paid;
  }, [program]);

  const onVideoClick = (video: ExerciseVideo) => {
    if (!isAuth) return openAuth();

    if (isNeedPay) {
      doOpenModal();
      return;
      // Это добавляло скролл к верху страницы при перевыборе видео
      // const player = document.getElementById('video');
      // isMobile && player?.scrollIntoView({ behavior: 'instant' });
      // onVideoClick(video);
    } else {
      doCloseModal();
    }

    if (isMobile && !getIsVideoAvailableByScheduleFunc(video)) {
      onNotAvailableByScheduleVideoClick(video);
      return;
    }

    const { is_paid, transaction_status } = program as Program;

    // if (!isAuthor && transaction_status === 'n') {
    //   return dispatch(
    //     openNotifyModal({
    //       title: 'Ожидание оплаты',
    //       text: 'Программа будем доступна через несколько минут. Обновите, пожалуйста, страницу',
    //       confirmText: 'Ок',
    //     })
    //   );
    // }

    // if (!isAuthor && (!transaction_status || transaction_status === 'f') && program) {
    //   return;
    // }

    if (isAuthor || is_paid) {
      saveCurrentPos();
      changeMode('video');
      setCurrentActiveRecommendation(undefined);
      setCurrentVideoInPlayer(video);
      setId(video.id);
      setDescription(video.description);
      setAbout(video.title);
      if (isMobile) {
        window.scrollTo(0, 0);
      }
    }
  };

  const onRecommendationClick = (recommendation: RecommendationT) => {
    changeMode('comment');
    setCurrentActiveRecommendation(recommendation);
    setDescription(recommendation?.recommendation_comment || '');
  };

  return {
    currentVideoInPlayer,
    // videoCode: code,
    onVideoClick,
    onAboutClick,
    onRecommendationClick,
    mode,
    setMode,
    changeMode,
    // source,
    // youtubeUrl,
    description,
    about,
    selectedId: id,
    isNeedPay,
    removeVideoId,
    scrollToSavedPos,
    videoBlockRef,
    saveCurrentPos,
    currentActiveRecommendation,
    isCurrentVideoAvailableBySchedule,
    onCurrentVideoNotAvailableClick,
    getIsVideoAvailableByScheduleFunc,
  };
};

export default useVideoClick;
